import React, { useState, useEffect, useRef } from 'react';   
import { ChevronDown, User, LogOut, Clock, Play, Square, Coffee, Utensils, Calendar, PauseCircle } from 'lucide-react';
import { attendanceApi } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from "../assets/Aarogya india hindia updated logo.svg";
import { authHelpers } from '../services/api';

import { useTheme } from '../context/ThemeContext';

function Header() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF' || currentTheme.color === '#0f172a';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null); // Ref for "Click Outside"
  const [attendance, setAttendance] = useState(null);
  const [liveDuration, setLiveDuration] = useState('0h 00m');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const user = authHelpers.getCurrentUser();

  // Click Outside Handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (user?.id) fetchStatus();
  }, [user?.id, location.pathname]); // Re-fetch on navigation to keep sync

  // Live Timer Effect
  useEffect(() => {
    let interval;
    if (attendance?.punchInTime && !attendance?.punchOutTime) {
      interval = setInterval(() => {
        calculateLiveTime(attendance.punchInTime);
      }, 1000);
    } else {
      setLiveDuration(attendance?.totalWorkHours || '0h 00m');
    }
    return () => clearInterval(interval);
  }, [attendance]);

  const calculateLiveTime = (startTimeStr) => {
    try {
      // Parse "09:30 AM" or "21:30" format
      const now = new Date();
      const [time, modifier] = startTimeStr.split(' ');
      let [hours, minutes] = time.split(':').map(Number);

      if (modifier === 'PM' && hours < 12) hours += 12;
      if (modifier === 'AM' && hours === 12) hours = 0;

      const startDate = new Date();
      startDate.setHours(hours, minutes, 0, 0);

      // Total seconds passed since punch in
      let diffMs = now - startDate;
      if (diffMs < 0) diffMs = 0; // Handle over-midnight or pre-punch

      const totalMinutes = Math.floor(diffMs / 60000);
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      const s = Math.floor((diffMs % 60000) / 1000);

      setLiveDuration(`${h}h ${m < 10 ? '0' + m : m}m ${s < 10 ? '0' + s : s}s`);
    } catch (e) {
      setLiveDuration(attendance?.totalWorkHours || '0h 00m');
    }
  };

  const fetchStatus = async () => {
    try {
      const res = await attendanceApi.getTodayAttendance(user.id);
      // Handle array or object
      const data = Array.isArray(res) ? res[0] : res;

      // Clean up "null" strings from API if any
      if (data) {
        if (data.punchOutTime === 'null' || data.punchOutTime === 'undefined') data.punchOutTime = null;
        if (data.punchInTime === 'null' || data.punchInTime === 'undefined') data.punchInTime = null;
      }

      setAttendance(data || null);
    } catch (err) {
      console.error('Header attendance fetch failed');
    }
  };

  const handleAction = async (actionFn) => {
    setLoading(true);
    try {
      await actionFn(user.id);
      await fetchStatus();
    } catch (err) {
      alert(err.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authHelpers.logout();
    navigate('/login');
  };

  return (
    <header 
      className={`sticky top-0 z-[9999] border-b-2 border-white/20 shadow-sm w-full h-[74px] opacity-100 no-print`}
      style={{ background: currentTheme.cssGradient }}
    >
      <div className="w-full h-full mx-auto px-6">
        <div className="flex items-center justify-between h-full">
          {/* Left: AAROGYA INDIA with logo icon */}
          <div className="flex items-center space-x-2 h-full py-1 relative z-10">
            {/* <img src={logo} alt="AAROGYA INDIA" className="w-48 md:w-56 lg:w-64 h-auto object-contain" /> */}
            {/* <img src={logo} alt="AAROGYA INDIA" className="w-32 md:w-40 h-auto object-contain" /> */}
          </div>

          {/* Quick Attendance Tracker */}

          {/* Right: User profile button (pill-shaped) */}
          <div className="relative" ref={dropdownRef}>
            <div
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center space-x-2 rounded-full px-3 py-2 cursor-pointer transition-colors bg-white border border-white"
            >
              <User className="w-5 h-5" style={{ color: currentTheme.color }} />
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} style={{ color: currentTheme.color }} />
            </div>

            {/* Logout Popup */}
            {isOpen && (
              <div className={`absolute right-0 top-full mt-2 w-48 rounded-lg shadow-xl py-2 z-50 ring-1 ring-black ring-opacity-5 animate-in fade-in slide-in-from-top-2 ${isDark ? 'bg-[#1e293b] border border-white/10' : 'bg-white'}`}>
                <div className={`px-4 py-2 border-b mb-1 ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>Account</p>
                </div>
                <button
                  onClick={() => {
                    navigate('/my-profile');
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center px-4 py-2 text-sm transition-colors ${isDark ? 'text-gray-200 hover:bg-white/10' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <User className="w-4 h-4 mr-2" />
                  My Profile
                </button>
                <button
                  onClick={handleLogout}
                  className={`w-full flex items-center px-4 py-2 text-sm transition-colors ${isDark ? 'text-red-400 hover:bg-red-500/20' : 'text-red-600 hover:bg-red-50'}`}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
